/**
 * Alerts while TrueWave is locked (the passphrase screen is up).
 *
 * Locking wipes the decryption key from memory, so the app can't read or
 * store anything — but it shouldn't go deaf either. This keeps a separate,
 * "locked" relay connection open: the relay hands it nothing, only an
 * `incoming` alert saying a message or call arrived (no sender, no content).
 * That's enough to ring or chime on the lock screen, like a phone; the real
 * envelopes stay queued at the relay and arrive the moment you unlock.
 *
 * Holds only the relay login (user id + token) in memory, never the vault key.
 */
import { create } from "zustand";
import { CALL_RING_TTL_MS } from "@truewave/protocol";
import { playMessageTone, playRingtone, vibrateRinging, type Tone } from "../calling/tones.js";
import { Channel } from "../transport/channel.js";

interface LockedAlertsState {
  /** A call is ringing (as far as the lock screen can tell). */
  ringing: boolean;
  /** Messages waiting to be read. */
  messages: number;
}

export const useLockedAlerts = create<LockedAlertsState>(() => ({ ringing: false, messages: 0 }));

const watcher = new Channel();
let running = false;
let ringtone: Tone | null = null;
let stopVibration: (() => void) | null = null;
let ringTimer: ReturnType<typeof setTimeout> | null = null;

function stopRinging(): void {
  ringtone?.stop();
  ringtone = null;
  stopVibration?.();
  stopVibration = null;
  if (ringTimer) clearTimeout(ringTimer);
  ringTimer = null;
  useLockedAlerts.setState({ ringing: false });
}

function onVisibility(): void {
  const visible = document.visibilityState === "visible";
  watcher.setActive(visible);
  if (visible) watcher.resume();
}

export function startLockWatch(creds: { userId: string; token: string }): void {
  if (running) return;
  running = true;
  useLockedAlerts.setState({ ringing: false, messages: 0 });
  watcher.connect(
    creds,
    {
      onDeliver: () => undefined, // the relay never delivers to a locked connection
      onIncoming: (f) => {
        const onScreen = document.visibilityState === "visible";
        // Shown right here → the relay needn't send a push as well. In the
        // background we stay quiet and let the push notification do the job.
        if (onScreen) watcher.seen(f.id);
        if (f.kind === "call") {
          useLockedAlerts.setState({ ringing: true });
          if (!ringtone) {
            ringtone = playRingtone();
            // Silent audio engine (iOS, app untouched since launch): buzz.
            if (!ringtone.audible) stopVibration = vibrateRinging();
          }
          // We can't read a "hung up" signal while locked, so stop when a real
          // phone would give up.
          if (ringTimer) clearTimeout(ringTimer);
          ringTimer = setTimeout(stopRinging, CALL_RING_TTL_MS);
        } else {
          useLockedAlerts.setState((s) => ({ messages: s.messages + 1 }));
          if (onScreen) playMessageTone();
        }
      },
    },
    "locked",
  );
  onVisibility();
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onVisibility);
}

/** Unlocking (or signing out): the full connection takes over. */
export function stopLockWatch(): void {
  if (!running) return;
  running = false;
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("online", onVisibility);
  watcher.disconnect();
  stopRinging();
  useLockedAlerts.setState({ messages: 0 });
}
